/**
 * Pro-Raum-Übersicht im 🎯-Detail-Panel (Sprint G2.1 — 2026-05-02).
 *
 * Strukturierte Bauteil-Sektionen pro Polygon:
 *   🟫 Boden   — BO-Codes
 *   🟦 Decke   — DE-Codes
 *   🟨 Wände   — AW/IW/WA/FAS-Codes (aggregiert pro Code, wallCount sichtbar)
 *   🚿 Sanitär — SAN-Codes + sanitaerObjekte[]-Liste
 *   🚪 Türen   — openings[type=door]
 *   🪟 Fenster — openings[type=window]
 *
 * Plus: 📋 LV-Positionen-Liste am Ende (aus aggregateRoomQuantities).
 *
 * Code-Picker pro Sektion filtert auf passende Gewerk-Prefixes:
 *   Boden    → BO
 *   Decke    → DE
 *   Wände    → AW, IW, FAS, WA
 *   Sanitär  → SAN
 */

import { useMemo, useState } from "react";
import {
  DEFAULT_CATALOG,
  getCatalogEntriesByGewerk,
  type GewerkPrefix,
} from "@bidi-takeoff/shared/gewerk-catalog";
import {
  aggregateRoomQuantities,
  type CatalogEntryForAggregator,
} from "@bidi-takeoff/lv-shared";
import type { PolygonFeature, SanitaerObjekt } from "../multiagent-types";
import { api } from "../api";
import { CollapsibleSection } from "./CollapsibleSection";

const SAN_TYPES = [
  { value: "WC", label: "WC" },
  { value: "WT", label: "Waschtisch" },
  { value: "DUSCHE", label: "Dusche" },
  { value: "BADEWANNE", label: "Badewanne" },
  { value: "URINAL", label: "Urinal" },
  { value: "KUECHENZEILE", label: "Küchenzeile" },
] as const;

type SanType = typeof SAN_TYPES[number]["value"];

const SECTION_GEWERKE: Record<string, GewerkPrefix[]> = {
  boden: ["BO"],
  decke: ["DE"],
  waende: ["AW", "IW", "FAS", "WA"],
  sanitaer: ["SAN"],
  tueren: ["TU"],
  fenster: ["FE"],
};

/**
 * Sprint G (2026-05-03) — bei outdoor-Polygonen (Terrasse/Dachterrasse/Balkon)
 * werden GA-Codes (Pflaster, Holzdiele, WPC, Steinplatte, Rasen, Kies) im
 * Boden-Code-Picker mit angeboten. Decke/Wände/Sanitär entfallen für GaLa-
 * Außenflächen (kein Gebäude-Volumen).
 */
const SECTION_GEWERKE_OUTDOOR: Record<string, GewerkPrefix[]> = {
  boden: ["BO", "GA"],
  decke: ["DE"],
  waende: ["AW", "IW", "FAS", "WA"],
  sanitaer: ["SAN"],
  tueren: ["TU"],
  fenster: ["FE"],
};

interface Props {
  polygon: PolygonFeature;
  runId: string;
  raumhoeheM: number;
  verschnittPct: number;
  /** Sprint G — true wenn polygon.isInsideBuilding === false (GaLa-Bereich). */
  isOutdoor?: boolean;
  onChange: () => void;
}

export function ProRaumUebersichtSection({
  polygon,
  runId,
  raumhoeheM,
  verschnittPct,
  isOutdoor,
  onChange,
}: Props) {
  const sectionGewerke = isOutdoor ? SECTION_GEWERKE_OUTDOOR : SECTION_GEWERKE;
  const aggregate = useMemo(
    () =>
      aggregateRoomQuantities(
        polygon as Parameters<typeof aggregateRoomQuantities>[0],
        DEFAULT_CATALOG as CatalogEntryForAggregator[],
        { defaultRaumhoeheM: raumhoeheM, verschnittPct }
      ),
    [polygon, raumhoeheM, verschnittPct]
  );

  const [pickerSection, setPickerSection] = useState<
    "boden" | "decke" | "waende" | "sanitaer" | "tueren" | "fenster" | null
  >(null);
  const [sanModalOpen, setSanModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (aggregate.isBuilding) {
    return (
      <div className="text-[11px] text-slate-500 italic mb-3">
        🏠 HAUS-Polygon — Pro-Raum-Übersicht entfällt (nur Außenkontur).
      </div>
    );
  }

  const polyId = polygon.properties.id;

  return (
    <div
      className="space-y-2 mb-3 border-l-2 pl-2"
      style={{
        borderColor: isOutdoor
          ? "var(--bd-ember, #d97757)"
          : "rgb(110 231 183)", /* emerald-300 */
      }}
    >
      <div className="text-[11px] font-semibold text-slate-700">
        {isOutdoor ? "🌿 GaLa-Bereich · Bauteil-Codes" : "📦 Bauteil-Codes"}
      </div>
      {isOutdoor && (
        <div className="text-[10px] text-slate-600 italic bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Außenbereich (Terrasse / Balkon / Stellplatz). Boden-Picker enthält
          GA-Codes (Pflaster, Holzdiele, WPC, Steinplatte, Rasen, Kies).
          Decke + Wände sind hier nicht relevant.
        </div>
      )}

      <SectionRow
        icon="🟫"
        label="Boden"
        codes={aggregate.sections.boden.codes}
        onAdd={() => setPickerSection("boden")}
      />
      <SectionRow
        icon="🟦"
        label="Decke"
        codes={aggregate.sections.decke.codes}
        onAdd={() => setPickerSection("decke")}
      />
      <SectionRow
        icon="🟨"
        label="Wände"
        codes={aggregate.sections.waende.codes}
        wallCount={polygon.properties.walls?.length}
        onAdd={() => setPickerSection("waende")}
      />
      <SectionRow
        icon="🚿"
        label="Sanitär"
        codes={aggregate.sections.sanitaer.codes}
        rightExtra={
          <span className="text-[10px] text-slate-500 ml-2">
            Objekte:{" "}
            {SAN_TYPES.filter(
              (t) => (aggregate.sections.sanitaer.sanCount[t.value] ?? 0) > 0
            ).map((t) => `${t.label}×${aggregate.sections.sanitaer.sanCount[t.value]}`)
              .join(" ") || "—"}
          </span>
        }
        extraButton={
          <button
            onClick={() => setSanModalOpen(true)}
            className="text-[10px] px-1.5 py-0.5 bg-cyan-100 hover:bg-cyan-200 text-cyan-800 border border-cyan-300 rounded ml-1"
          >
            + Objekt
          </button>
        }
        onAdd={() => setPickerSection("sanitaer")}
      />

      {/* Türen-Section: Codes (TU) + Openings */}
      <SectionRow
        icon="🚪"
        label="Türen"
        codes={aggregate.sections.tueren.codes}
        rightExtra={
          aggregate.sections.tueren.openings.length > 0 && (
            <span
              className="text-[10px] text-slate-500 ml-2"
              title={aggregate.sections.tueren.openings
                .map(
                  (t) =>
                    `${t.label ?? ""} ${(t.widthM * 100).toFixed(0)}×${((t.heightM ?? 2.01) * 100).toFixed(0)}`
                )
                .join(" / ")}
            >
              {aggregate.sections.tueren.openings.length}× im Plan
            </span>
          )
        }
        onAdd={() => setPickerSection("tueren")}
      />

      {/* Fenster-Section: Codes (FE) + Openings */}
      <SectionRow
        icon="🪟"
        label="Fenster"
        codes={aggregate.sections.fenster.codes}
        rightExtra={
          aggregate.sections.fenster.openings.length > 0 && (
            <span
              className="text-[10px] text-slate-500 ml-2"
              title={aggregate.sections.fenster.openings
                .map(
                  (f) =>
                    `${f.label ?? ""} ${(f.widthM * 100).toFixed(0)}×${((f.heightM ?? 1.0) * 100).toFixed(0)}` +
                    (f.bruestungM != null
                      ? ` BRH ${(f.bruestungM * 100).toFixed(0)}`
                      : "")
                )
                .join(" / ")}
            >
              {aggregate.sections.fenster.openings.length}× im Plan
            </span>
          )
        }
        onAdd={() => setPickerSection("fenster")}
      />

      {/* LV-Positionen-Liste — Sprint UX (2026-05-06) einklappbar, default zu.
       *  Frank prüft Mengen meist erst am Ende, nicht pro Raum-Klick. */}
      {aggregate.lvPositions.length > 0 && (
        <CollapsibleSection
          icon="📋"
          title="LV-Positionen"
          counter={aggregate.lvPositions.length}
          hint="Aggregierte LV-Positionen für diesen Raum (aus den Bauteil-Codes)"
        >
          <div className="space-y-0.5 mt-2 max-h-[260px] overflow-y-auto">
            {aggregate.lvPositions.map((p, i) => (
              <div
                key={`${p.code}-${p.bezeichnung}-${i}`}
                className="text-[11px] bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                title={`Formel: ${p.mengenFormel}\nAusdruck: ${p.mengenAusdruck}\n\n${p.langtextTemplate}`}
              >
                <div className="flex justify-between items-baseline gap-2">
                  <span className="font-mono text-slate-700 text-[10px]">
                    {p.ozPattern.replace("{N}", "??")}
                  </span>
                  <span className="font-mono text-emerald-700">
                    {p.menge.toFixed(2)} {p.einheit}
                  </span>
                </div>
                <div className="text-slate-700 truncate">{p.bezeichnung}</div>
                <div className="text-[10px] text-slate-400">
                  {p.code} · {p.scope} · {p.source}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {(aggregate.unmappedCodes.length > 0 ||
        aggregate.unknownCodes.length > 0) && (
        <details className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1">
          <summary>
            {aggregate.unmappedCodes.length} ohne LV-Mapping ·{" "}
            {aggregate.unknownCodes.length} unbekannt
          </summary>
          {aggregate.unmappedCodes.map((c, i) => (
            <div key={`u-${i}`}>
              · {c.code} ({c.scope}) — kein lvMapping befüllt
            </div>
          ))}
          {aggregate.unknownCodes.map((c, i) => (
            <div key={`x-${i}`}>· {c.code} ({c.scope}) — nicht im Katalog</div>
          ))}
        </details>
      )}

      {/* Code-Picker-Modal */}
      {pickerSection && (
        <CodePickerModal
          section={pickerSection}
          gewerke={sectionGewerke[pickerSection]}
          onClose={() => setPickerSection(null)}
          onPick={async (code) => {
            setBusy(true);
            try {
              await api.multiagentAddPolygonCodeAssignment(runId, polyId, {
                code,
                source: "user-manual",
              });
              setPickerSection(null);
              onChange();
            } catch (err) {
              window.alert(
                "Code zuweisen fehlgeschlagen: " +
                  (err instanceof Error ? err.message : String(err))
              );
            } finally {
              setBusy(false);
            }
          }}
          busy={busy}
        />
      )}

      {sanModalOpen && (
        <SanitaerObjektModal
          onClose={() => setSanModalOpen(false)}
          onSave={async (body) => {
            setBusy(true);
            try {
              await api.multiagentAddSanitaerObjekt(runId, polyId, body);
              setSanModalOpen(false);
              onChange();
            } catch (err) {
              window.alert(
                "Objekt anlegen fehlgeschlagen: " +
                  (err instanceof Error ? err.message : String(err))
              );
            } finally {
              setBusy(false);
            }
          }}
          busy={busy}
        />
      )}

      {/* Sanitär-Objekt-Liste mit Delete-Button */}
      {aggregate.sections.sanitaer.objekte.length > 0 && (
        <details className="text-[10px] text-cyan-800">
          <summary className="cursor-pointer">
            Sanitär-Objekt-Details ({aggregate.sections.sanitaer.objekte.length})
          </summary>
          <ul className="ml-2 mt-1 space-y-0.5">
            {aggregate.sections.sanitaer.objekte.map((obj) => (
              <li
                key={(obj as SanitaerObjekt).id}
                className="flex justify-between items-center"
              >
                <span>
                  · {obj.type}
                  {obj.hersteller ? ` (${obj.hersteller}${obj.modell ? " " + obj.modell : ""})` : ""}
                </span>
                <button
                  onClick={async () => {
                    if (!window.confirm(`${obj.type} löschen?`)) return;
                    try {
                      await api.multiagentDeleteSanitaerObjekt(
                        runId,
                        polyId,
                        (obj as SanitaerObjekt).id
                      );
                      onChange();
                    } catch (err) {
                      window.alert(
                        "Löschen fehlgeschlagen: " +
                          (err instanceof Error ? err.message : String(err))
                      );
                    }
                  }}
                  className="text-red-600 hover:text-red-800 text-[10px]"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ============================================================================
// Sub-Components
// ============================================================================

function SectionRow({
  icon,
  label,
  codes,
  wallCount,
  onAdd,
  rightExtra,
  extraButton,
}: {
  icon: string;
  label: string;
  codes: { code: string; gewerk: string; source: string; wallCount?: number; shortLabel?: string }[];
  wallCount?: number;
  onAdd: () => void;
  rightExtra?: React.ReactNode;
  extraButton?: React.ReactNode;
}) {
  return (
    <div className="text-[11px] flex items-baseline flex-wrap gap-1">
      <span className="w-4">{icon}</span>
      <span className="text-slate-700 font-medium w-12">{label}</span>
      {wallCount != null && (
        <span className="text-[10px] text-slate-500">{wallCount} Wand</span>
      )}
      {codes.length === 0 ? (
        <span className="text-slate-400">—</span>
      ) : (
        <span className="flex flex-wrap gap-1">
          {codes.map((c, i) => (
            <span
              key={`${c.code}-${c.source}-${i}`}
              className={`text-[10px] px-1 py-0.5 rounded font-mono border ${
                c.source === "user-manual"
                  ? "bg-blue-50 border-blue-300 text-blue-800"
                  : "bg-slate-50 border-slate-300 text-slate-700"
              }`}
              title={`${c.shortLabel ?? c.code} · ${c.source}`}
            >
              {c.code}
              {c.wallCount && c.wallCount > 1 ? ` (${c.wallCount}×)` : ""}
            </span>
          ))}
        </span>
      )}
      {rightExtra}
      <span className="ml-auto flex items-center gap-1">
        <button
          onClick={onAdd}
          className="text-[10px] px-1.5 py-0.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-300 rounded"
        >
          + Code
        </button>
        {extraButton}
      </span>
    </div>
  );
}

function CodePickerModal({
  section,
  gewerke,
  onClose,
  onPick,
  busy,
}: {
  section: "boden" | "decke" | "waende" | "sanitaer" | "tueren" | "fenster";
  gewerke: GewerkPrefix[];
  onClose: () => void;
  onPick: (code: string) => void;
  busy: boolean;
}) {
  const candidates = useMemo(() => {
    return gewerke.flatMap((g) => getCatalogEntriesByGewerk(g));
  }, [gewerke]);
  const [search, setSearch] = useState("");
  const filtered = candidates.filter(
    (c) =>
      c.code.toLowerCase().includes(search.toLowerCase()) ||
      (c.shortLabel ?? "").toLowerCase().includes(search.toLowerCase()) ||
      c.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between border-b p-3">
          <div className="text-sm font-semibold">
            Code zuweisen — Section {section} ({gewerke.join("/")})
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            ×
          </button>
        </div>
        <input
          autoFocus
          type="text"
          placeholder="Suche Code / Label / Beschreibung..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="text-sm m-3 px-2 py-1 border border-slate-300 rounded"
        />
        <div className="overflow-y-auto px-3 pb-3 space-y-1">
          {filtered.length === 0 && (
            <div className="text-xs text-slate-500 italic">
              Kein Code für diese Sektion gefunden.
            </div>
          )}
          {filtered.map((c) => (
            <button
              key={c.code}
              onClick={() => onPick(c.code)}
              disabled={busy}
              className="w-full text-left text-xs px-2 py-1.5 border border-slate-200 rounded hover:bg-emerald-50 disabled:opacity-50"
            >
              <div className="flex justify-between gap-2">
                <span className="font-mono text-slate-700">{c.code}</span>
                <span className="text-slate-500 text-[10px]">{c.gewerk}</span>
              </div>
              <div className="text-slate-700">{c.shortLabel ?? c.fullCode}</div>
              <div className="text-[10px] text-slate-500 truncate">
                {c.description}
              </div>
              {c.lvMapping && c.lvMapping.positions.length > 0 ? (
                <div className="text-[10px] text-emerald-700">
                  ✓ {c.lvMapping.positions.length} LV-Positionen
                </div>
              ) : (
                <div className="text-[10px] text-amber-600">
                  ⚠ kein LV-Mapping befüllt
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SanitaerObjektModal({
  onClose,
  onSave,
  busy,
}: {
  onClose: () => void;
  onSave: (body: {
    type: SanType;
    hersteller?: string;
    modell?: string;
    breiteCm?: number;
    tiefeCm?: number;
    hoeheCm?: number;
    bemerkung?: string;
  }) => void;
  busy: boolean;
}) {
  const [type, setType] = useState<SanType>("WC");
  const [hersteller, setHersteller] = useState("");
  const [modell, setModell] = useState("");
  const [breiteCm, setBreiteCm] = useState("");
  const [tiefeCm, setTiefeCm] = useState("");
  const [hoeheCm, setHoeheCm] = useState("");
  const [bemerkung, setBemerkung] = useState("");

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between border-b p-3">
          <div className="text-sm font-semibold">+ Sanitär-Objekt</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            ×
          </button>
        </div>
        <div className="p-3 space-y-2">
          <label className="block text-xs">
            Type
            <select
              value={type}
              onChange={(e) => setType(e.target.value as SanType)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-sm"
            >
              {SAN_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs">
            Hersteller (optional)
            <input
              type="text"
              value={hersteller}
              onChange={(e) => setHersteller(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-sm"
              placeholder="Geberit / Villeroy / Duravit ..."
            />
          </label>
          <label className="block text-xs">
            Modell (optional)
            <input
              type="text"
              value={modell}
              onChange={(e) => setModell(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-sm"
            />
          </label>
          <div className="grid grid-cols-3 gap-2">
            <label className="block text-xs">
              Breite (cm)
              <input
                type="number"
                value={breiteCm}
                onChange={(e) => setBreiteCm(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-sm"
              />
            </label>
            <label className="block text-xs">
              Tiefe (cm)
              <input
                type="number"
                value={tiefeCm}
                onChange={(e) => setTiefeCm(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-sm"
              />
            </label>
            <label className="block text-xs">
              Höhe (cm)
              <input
                type="number"
                value={hoeheCm}
                onChange={(e) => setHoeheCm(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-sm"
              />
            </label>
          </div>
          <label className="block text-xs">
            Bemerkung (optional)
            <textarea
              value={bemerkung}
              onChange={(e) => setBemerkung(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-sm"
              rows={2}
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t p-3">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded"
          >
            Abbrechen
          </button>
          <button
            onClick={() => {
              const body: Parameters<typeof onSave>[0] = { type };
              if (hersteller.trim()) body.hersteller = hersteller.trim();
              if (modell.trim()) body.modell = modell.trim();
              const bn = Number(breiteCm);
              if (Number.isFinite(bn) && bn > 0) body.breiteCm = bn;
              const tn = Number(tiefeCm);
              if (Number.isFinite(tn) && tn > 0) body.tiefeCm = tn;
              const hn = Number(hoeheCm);
              if (Number.isFinite(hn) && hn > 0) body.hoeheCm = hn;
              if (bemerkung.trim()) body.bemerkung = bemerkung.trim();
              onSave(body);
            }}
            disabled={busy}
            className="text-xs px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded disabled:opacity-50"
          >
            {busy ? "Speichert…" : "Anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
}
